import React, { useState, useEffect } from 'react';
import { X, Users, UserPlus, Check, Search, Loader2, Share2 } from 'lucide-react';
import { collection, query, getDocs, doc, updateDoc, getDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { toast } from 'react-hot-toast';
import { useThemeStore } from '../stores/themeStore';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  photoURL?: string;
}

interface CalendarSharingProps {
  onClose: () => void;
}

export default function CalendarSharing({ onClose }: CalendarSharingProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [sharedUsers, setSharedUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { darkMode } = useThemeStore();

  useEffect(() => {
    loadUsers();
    loadSharedUsers();
  }, []);

  const loadUsers = async () => {
    if (!auth.currentUser) return;

    try {
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      
      const usersList: User[] = [];
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Ne pas inclure l'utilisateur actuel dans la liste
        if (doc.id !== auth.currentUser?.uid) {
          usersList.push({
            id: doc.id,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            role: data.role,
            photoURL: data.photoURL
          });
        }
      });
      
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSharedUsers = async () => {
    if (!auth.currentUser) return;

    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const sharedWithIds = userData.calendarSharedWith || [];
        
        // Charger les informations des utilisateurs avec qui le calendrier est partagé
        const sharedWithUsers: User[] = [];
        
        for (const userId of sharedWithIds) {
          const sharedUserRef = doc(db, 'users', userId);
          const sharedUserDoc = await getDoc(sharedUserRef);
          
          if (sharedUserDoc.exists()) {
            const sharedUserData = sharedUserDoc.data();
            sharedWithUsers.push({
              id: sharedUserDoc.id,
              email: sharedUserData.email,
              firstName: sharedUserData.firstName,
              lastName: sharedUserData.lastName,
              role: sharedUserData.role,
              photoURL: sharedUserData.photoURL
            });
          }
        }
        
        setSharedUsers(sharedWithUsers);
      }
    } catch (error) {
      console.error('Error loading shared users:', error);
      toast.error('Erreur lors du chargement des partages');
    }
  };

  const handleShareCalendar = async (user: User) => {
    if (!auth.currentUser) return;
    
    setIsSaving(true);
    
    try {
      // Mettre à jour le document de l'utilisateur actuel pour ajouter l'utilisateur à la liste de partage
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        calendarSharedWith: arrayUnion(user.id)
      });
      
      // Mettre à jour le document de l'utilisateur avec qui on partage pour ajouter l'accès
      const sharedUserRef = doc(db, 'users', user.id);
      await updateDoc(sharedUserRef, {
        hasAccessToCalendars: arrayUnion(auth.currentUser.uid)
      });
      
      // Mettre à jour l'état local
      setSharedUsers([...sharedUsers, user]);
      
      toast.success(`Calendrier partagé avec ${user.email}`);
    } catch (error) {
      console.error('Error sharing calendar:', error);
      toast.error('Erreur lors du partage du calendrier');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveShare = async (user: User) => {
    if (!auth.currentUser) return;
    
    setIsSaving(true);
    
    try {
      // Mettre à jour le document de l'utilisateur actuel pour retirer l'utilisateur de la liste de partage
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        calendarSharedWith: arrayRemove(user.id)
      });
      
      // Mettre à jour le document de l'utilisateur avec qui on partage pour retirer l'accès
      const sharedUserRef = doc(db, 'users', user.id);
      await updateDoc(sharedUserRef, {
        hasAccessToCalendars: arrayRemove(auth.currentUser.uid)
      });
      
      // Mettre à jour l'état local
      setSharedUsers(sharedUsers.filter(u => u.id !== user.id));
      
      toast.success(`Partage retiré pour ${user.email}`);
    } catch (error) {
      console.error('Error removing share:', error);
      toast.error('Erreur lors du retrait du partage');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredUsers = users.filter(user => {
    // Exclure les utilisateurs déjà dans la liste de partage
    if (sharedUsers.some(sharedUser => sharedUser.id === user.id)) {
      return false;
    }
    
    // Filtrer par terme de recherche
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      (user.firstName && user.firstName.toLowerCase().includes(searchLower)) ||
      (user.lastName && user.lastName.toLowerCase().includes(searchLower)) ||
      (user.role && user.role.toLowerCase().includes(searchLower))
    );
  });

  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email.split('@')[0];
  };

  const getUserInitials = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center">
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className={`text-xl font-semibold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Share2 className="w-5 h-5 mr-2 text-blue-600" />
              Partager mon calendrier
            </h2>
            <button onClick={onClose} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Liste des utilisateurs avec qui le calendrier est partagé */}
          <div className="mb-6">
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Partagé avec ({sharedUsers.length})
            </h3>
            
            {sharedUsers.length === 0 ? (
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Votre calendrier n'est partagé avec personne pour le moment.
              </p>
            ) : (
              <div className="space-y-2">
                {sharedUsers.map(user => (
                  <div 
                    key={user.id} 
                    className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-3">
                      {user.photoURL ? (
                        <img 
                          src={user.photoURL} 
                          alt={getUserDisplayName(user)} 
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                          {getUserInitials(user)}
                        </div>
                      )}
                      <div>
                        <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {getUserDisplayName(user)}
                        </div>
                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {user.email} {user.role && `• ${user.role}`}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveShare(user)}
                      disabled={isSaving}
                      className={`px-3 py-1 text-xs rounded-lg ${darkMode ? 'bg-red-900 text-red-300 hover:bg-red-800' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                    >
                      Retirer
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recherche d'utilisateurs */}
          <div>
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Ajouter des utilisateurs
            </h3>
            
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} rounded-lg`}
              />
            </div>
            
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className={`w-8 h-8 animate-spin ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map(user => (
                    <div 
                      key={user.id} 
                      className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}
                    >
                      <div className="flex items-center gap-3">
                        {user.photoURL ? (
                          <img 
                            src={user.photoURL} 
                            alt={getUserDisplayName(user)} 
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                            {getUserInitials(user)}
                          </div>
                        )}
                        <div>
                          <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {getUserDisplayName(user)}
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {user.email} {user.role && `• ${user.role}`}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleShareCalendar(user)}
                        disabled={isSaving}
                        className={`flex items-center px-3 py-1 text-xs rounded-lg ${darkMode ? 'bg-blue-900 text-blue-300 hover:bg-blue-800' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        Partager
                      </button>
                    </div>
                  ))
                ) : (
                  <p className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {searchTerm ? 'Aucun utilisateur trouvé' : 'Commencez à taper pour rechercher des utilisateurs'}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'} rounded-lg`}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}